import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Code, Email, Excel, Image, Pdf, RTF, Word } from '@fluentsms/agentnet-web-icons';
import { Grid, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import { useEffect, useState } from 'react';

import { Document, DocumentFPX, DocumentPXT, DocumentTXT } from 'ui-kit/icons/DocIcons';
import { AgentNetCheckBox } from 'ui-kit/inputs';
import AgentNetButton from '../button/AgentNetButton';

interface DocumentItem {
  id: string;
  name: string;
  description: string;
  lastModifiedDate: string;
  uri: string;
  type: string;
  canDownloadAsZipPdf: boolean;
  canViewDocument: boolean;
  isChecked?: boolean;
  canDownLoad?: boolean;
}

interface ServiceOrderDocumentsTable {
  documents?: Array<any>;
  disabled?: boolean;
  onRowCheck?: (value: any, documentURI: string) => void;
  onDelete?: (documentURI: string) => void;
  onViewDocument?: any;
  onDownloadPdf?: (documentURI: string[]) => void;
  onDownloadZip?: (documentURI: string[]) => void;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    documentsTable: {
      width: '100%',
      padding: '0 0 24px',
    },
    documentsTableHeader: {
      backgroundColor: '#F2F5F6',
      borderBottom: '1px solid #D9E0E5',
      fontWeight: 600,
      fontSize: pxToRem(14),
    },
    tableHeaderCell: {
      padding: pxToRem(12),
    },
    documentsTableList: {},
    tableRow: {
      borderBottom: `1px solid #E0E0E0`,
    },
    tableCell: {
      padding: pxToRem(8),
      display: 'flex',
      alignItems: 'center',
    },
    deleteRow: {
      padding: 0,
      marginLeft: theme.spacing(1),
      '&:hover': {
        backgroundColor: 'transparent',
      },
    },
    checkbox: {
      marginRight: theme.spacing(1),
    },
    incorporatedCell: {
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
    },
    incorporatedCellDescription: {
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
    },
    my1: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    mb1: {
      marginBottom: theme.spacing(1),
    },
    actions: {
      '& .MuiAgentNetButton': {
        marginRight: theme.spacing(2),
      },
    },
    deleteCell: {
      justifyContent: 'space-between',
    },
    rowChecked: {
      backgroundColor: '#FDF8CF',
    },
  }),
);

const ServiceOrderDocumentsTable = ({
  documents,
  disabled,
  onRowCheck,
  onViewDocument,
  onDownloadPdf,
  onDownloadZip,
}: ServiceOrderDocumentsTable) => {
  const classes = useStyles();
  const ICON_PROPS = { width: 18, height: 18, fill: '#0074CA' };

  const [items, setItems] = useState<DocumentItem[]>([]);
  const [selectAllDocuments, setSelectAllDocuments] = useState(false);

  useEffect(() => {
    if (documents && documents.length > 0) {
      const newDocs = documents?.map((d) => {
        return {
          ...d,
          isChecked: false,
        };
      });
      setItems(newDocs);
    }
  }, [documents]);

  function generateIcon(fileType?: any) {
    const lowerCaseFileType = fileType?.toLowerCase();

    if (lowerCaseFileType === 'pdf') {
      return <Pdf {...ICON_PROPS} />;
    }
    if (lowerCaseFileType === 'xls' || lowerCaseFileType === 'xlsx') {
      return <Excel {...ICON_PROPS} />;
    }
    if (lowerCaseFileType === 'docx' || lowerCaseFileType === 'doc') {
      return <Word {...ICON_PROPS} />;
    }
    if (lowerCaseFileType === 'txt') {
      return <DocumentTXT />;
    }
    if (
      lowerCaseFileType === 'tiff' ||
      lowerCaseFileType === 'tif' ||
      lowerCaseFileType === 'jpg' ||
      lowerCaseFileType === 'jpeg' ||
      lowerCaseFileType === 'png' ||
      lowerCaseFileType === 'bmp' ||
      lowerCaseFileType === 'gif'
    ) {
      return <Image {...ICON_PROPS} />;
    }
    if (lowerCaseFileType === 'rtf') {
      return <RTF {...ICON_PROPS} />;
    }
    if (lowerCaseFileType === 'htm' || lowerCaseFileType === 'xml') {
      return <Code {...ICON_PROPS} />;
    }
    if (lowerCaseFileType === 'msg') {
      return <Email {...ICON_PROPS} />;
    }
    if (lowerCaseFileType === 'pxt') {
      return <DocumentPXT />;
    }
    if (lowerCaseFileType === 'fpx') {
      return <DocumentFPX />;
    }
    return <Document />;
  }

  const handleRowCheck = (checked: boolean, uri: any) => {
    if (uri === 'selectAll') {
      setSelectAllDocuments(checked);
      onRowCheck && onRowCheck(checked, uri);
      const newObj = items.map((doc) => {
        if (doc.canDownloadAsZipPdf && doc.canViewDocument) {
          return { ...doc, isChecked: checked };
        }
        return doc;
      });
      setItems(newObj);
    } else {
      onRowCheck && onRowCheck(checked, uri);
      const newObj = items.map((doc) => {
        if (doc.uri == uri) {
          return { ...doc, isChecked: !doc.isChecked };
        }
        return doc;
      });
      setItems(newObj);
    }
  };

  const handleDownload = (payload: string) => {
    const selected: any = [];
    items.forEach((i) => {
      if (i.isChecked) {
        selected.push(i.uri);
      }
    });
    if (payload === 'pdf') {
      onDownloadPdf && onDownloadPdf(selected);
    }
    if (payload === 'zip') {
      onDownloadZip && onDownloadZip(selected);
    }
  };

  return (
    <>
      {items && items.length > 0 && (
        <>
          <div className={clsx(classes.documentsTable, 'documentsTableWrapper')}>
            <Grid container className={classes.documentsTableHeader}>
              <Grid item sm={4} className={classes.tableHeaderCell}>
                <AgentNetCheckBox
                  value={selectAllDocuments}
                  checkHandler={(el) => handleRowCheck(el, 'selectAll')}
                  className={classes.checkbox}
                  disabled={disabled}
                />
                Document Name
              </Grid>
              <Grid item sm={3} className={classes.tableHeaderCell}>
                Description
              </Grid>
              <Grid item sm={4} className={classes.tableHeaderCell}>
                Date
              </Grid>
              <Grid item sm={1} className={classes.tableHeaderCell}>
                File
              </Grid>
            </Grid>
            <Grid container className={classes.documentsTableList}>
              {items.map((e: any, i) => (
                <Grid
                  container
                  key={i}
                  className={clsx(classes.tableRow, { [classes.rowChecked]: e.isChecked === true })}
                >
                  <Grid item sm={4} className={clsx(classes.tableCell)}>
                    <AgentNetCheckBox
                      value={e.isChecked}
                      checkHandler={(el) => handleRowCheck(el, e.uri)}
                      className={classes.checkbox}
                      disabled={!(e.canDownloadAsZipPdf && e.canViewDocument)}
                    />
                    <Typography variant="body2">{e.name}</Typography>
                  </Grid>
                  <Grid item sm={3} className={clsx(classes.tableCell)}>
                    <Typography variant="body2">{e.description}</Typography>
                  </Grid>
                  <Grid item sm={4} className={clsx(classes.tableCell)}>
                    <Typography variant="body2">{e.lastModifiedDate}</Typography>
                  </Grid>
                  <Grid item sm={1} className={clsx(classes.tableCell)}>
                    <AgentNetButton
                      variant="text"
                      onClick={(e1) => onViewDocument(e)}
                      data-testid="ServiceOrderDocumentView"
                      disabled={!e.canViewDocument}
                    >
                      {e.canViewDocument && generateIcon(e.mimeType)}
                    </AgentNetButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </div>
          <div className={classes.actions}>
            <AgentNetButton
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => handleDownload('pdf')}
              disabled={false}
              data-testid="ServiceOrderDocumentDownloadAsPDF"
            >
              Download as PDF
            </AgentNetButton>
            <AgentNetButton
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleDownload('zip')}
              disabled={false}
              data-testid="ServiceOrderDocumentDownloadasZIP"
            >
              Download as ZIP
            </AgentNetButton>
          </div>
        </>
      )}
    </>
  );
};

export default ServiceOrderDocumentsTable;
